import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, StatusBadge, GsdReadableBlock } from "@/coquille-bridge";
import { DataTable } from "@/components/DataTable.jsx";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { useDataStore, notifyDataUpdate } from "@/components/admin/useDataStore.js";
import {
  addDocumentImport,
  updateDocumentImport,
  addDocumentImportWarning,
  confirmDocumentImport,
  cancelDocumentImport,
} from "@/data/dataStore.js";
import {
  DOCUMENT_TYPES,
  SENSITIVITY_LEVELS,
  IMPORT_STATUSES,
  authDocLabel,
} from "@/utils/authorityDocumentLabels.js";
import {
  IMPORT_INITIAL_VALIDATION_STATUS,
  IMPORT_DEFAULT_SENSITIVITY,
  IMPORT_PREVIEW_NOTICE_FR,
  IMPORT_PREVIEW_NOTICE_EN,
  IMPORT_PREVIEW_OFFICIAL_NOTICE_FR,
  IMPORT_PREVIEW_OFFICIAL_NOTICE_EN,
} from "@/data/documentImportData.js";
import { localizeDemoText, formatImportFileDisplay } from "@/i18n/localizeDemoText.js";

function useLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

const CATEGORIES = {
  autorite: { fr: "Autorité", en: "Authority" },
  demande_titre: { fr: "Demande de titre", en: "Title application" },
  reconnaissance: { fr: "Reconnaissance", en: "Recognition" },
  dojo_general: { fr: "Dojo général", en: "General dojo" },
  autre: { fr: "Autre", en: "Other" },
};

const MODULES = ["Shotokan Black Dragon", "Dojo général"];

function buildWarnings(form, locale) {
  const w = [];
  if (form.sensibilite === "tres_confidentiel" || form.sensibilite === "confidentiel") {
    w.push({
      niveau: "attention",
      message:
        locale === "en"
          ? "Sensitive document — restricted access required in Phase 2."
          : "Document sensible — accès restreint requis en Phase 2.",
      bloquant: false,
      action_recommandee: locale === "en" ? "Limit visibility to Hanshi/Admin." : "Limiter la visibilité à Hanshi/Admin.",
    });
  }
  if (!form.dossier_lie) {
    w.push({
      niveau: "attention",
      message:
        locale === "en"
          ? "No linked dossier — the document will not be attached to any proof."
          : "Aucun dossier lié — le document ne sera rattaché à aucune preuve.",
      bloquant: false,
      action_recommandee: locale === "en" ? "Link to an evidence dossier if applicable." : "Lier à un dossier de preuves si applicable.",
    });
  }
  if (!form.type_document || form.type_document === "autre") {
    w.push({
      niveau: "info",
      message: locale === "en" ? "Uncertain document type." : "Type de document incertain.",
      bloquant: false,
      action_recommandee: locale === "en" ? "Confirm the document type." : "Confirmer le type de document.",
    });
  }
  w.push({
    niveau: "info",
    message:
      locale === "en"
        ? "Unvalidated official proof — status received — validation required is enforced."
        : "Preuve officielle non validée — statut reçu — validation requise imposé.",
    bloquant: false,
    action_recommandee: locale === "en" ? "Have it validated by a competent authority." : "Faire valider par une autorité compétente.",
  });
  w.push({
    niveau: "info",
    message: locale === "en" ? "File not stored on any server." : "Fichier non stocké côté serveur.",
    bloquant: false,
    action_recommandee: locale === "en" ? "Real storage planned for a secure backend." : "Stockage réel prévu pour un backend sécurisé.",
  });
  return w;
}

const EMPTY_FORM = {
  fichier_nom: "",
  fichier_type: "application/pdf",
  taille_affichee: "",
  type_document: "autre",
  categorie: "autre",
  sensibilite: IMPORT_DEFAULT_SENSITIVITY,
  module_lie: "Shotokan Black Dragon",
  dossier_lie: "",
  notes: "",
};

/** Formulaire d'import contrôlé + aperçu + confirmation (mock/local). */
export function DocumentImportPanel({ testId = "document-import-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);

  const dossiers = store.evidenceDossiers || [];

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handlePreview = () => {
    const warnings = buildWarnings(form, locale);
    setPreview({ ...form, warnings });
  };

  const handleConfirm = () => {
    if (!preview) return;
    const importId = `import-${Date.now()}`;
    const warningIds = preview.warnings.map((_, i) => `warn-${importId}-${i}`);

    addDocumentImport({
      id: importId,
      fichier_nom: preview.fichier_nom || "[DÉMO] document-sans-nom.pdf",
      fichier_type: preview.fichier_type,
      taille_affichee: preview.taille_affichee || "— (métadonnée)",
      categorie_proposee: preview.categorie,
      type_document_propose: preview.type_document,
      sensibilite_proposee: preview.sensibilite,
      dossier_lie_propose: preview.dossier_lie || null,
      module_lie: preview.module_lie,
      statut_import: "apercu_genere",
      statut_validation_initial: IMPORT_INITIAL_VALIDATION_STATUS,
      source: "import client",
      ajoute_par: "admin",
      date_ajout: new Date().toISOString().slice(0, 10),
      avertissements: warningIds,
      notes: preview.notes || "Import local — fichier non stocké officiellement.",
      demo: false,
    });
    preview.warnings.forEach((w, i) => {
      addDocumentImportWarning({ id: warningIds[i], import_id: importId, ...w });
    });

    confirmDocumentImport(importId, "admin", "hanshi");
    notifyDataUpdate();
    setPreview(null);
    setForm(EMPTY_FORM);
  };

  const handleCancel = () => {
    setPreview(null);
    setForm(EMPTY_FORM);
  };

  return (
    <div data-testid={testId}>
      <GsdReadableBlock testId={`${testId}-intro`}>
        <h3>{locale === "en" ? "Controlled import" : "Import contrôlé"}</h3>
        <p className="muted" data-testid={`${testId}-no-server-storage`}>
          {locale === "en"
            ? "Local demo — only metadata is handled. No real file is uploaded or stored (no server storage)."
            : "Démo locale — seules les métadonnées sont traitées. Aucun fichier réel n'est téléversé ni stocké (aucun stockage serveur)."}
        </p>
      </GsdReadableBlock>

      <GsdReadableBlock testId={`${testId}-form`}>
        <h4>{locale === "en" ? "Import form (metadata)" : "Formulaire d'import (métadonnées)"}</h4>
        <div className="form-grid">
          <label>
            {locale === "en" ? "File name" : "Nom du fichier"}
            <input
              value={form.fichier_nom}
              onChange={(e) => setField("fichier_nom", e.target.value)}
              data-testid={`${testId}-input-nom`}
              placeholder={locale === "en" ? "e.g. certificate.pdf" : "ex. certificat.pdf"}
            />
          </label>
          <label>
            {locale === "en" ? "Document type" : "Type de document"}
            <select value={form.type_document} onChange={(e) => setField("type_document", e.target.value)} data-testid={`${testId}-input-type`}>
              {Object.entries(DOCUMENT_TYPES).map(([v, l]) => (
                <option key={v} value={v}>{locale === "en" ? l.en : l.fr}</option>
              ))}
            </select>
          </label>
          <label>
            {locale === "en" ? "Category" : "Catégorie"}
            <select value={form.categorie} onChange={(e) => setField("categorie", e.target.value)} data-testid={`${testId}-input-categorie`}>
              {Object.entries(CATEGORIES).map(([v, l]) => (
                <option key={v} value={v}>{locale === "en" ? l.en : l.fr}</option>
              ))}
            </select>
          </label>
          <label>
            {locale === "en" ? "Sensitivity" : "Sensibilité"}
            <select value={form.sensibilite} onChange={(e) => setField("sensibilite", e.target.value)} data-testid={`${testId}-input-sensibilite`}>
              {Object.entries(SENSITIVITY_LEVELS).map(([v, l]) => (
                <option key={v} value={v}>{locale === "en" ? l.en : l.fr}</option>
              ))}
            </select>
          </label>
          <label>
            {locale === "en" ? "Linked module" : "Module lié"}
            <select value={form.module_lie} onChange={(e) => setField("module_lie", e.target.value)} data-testid={`${testId}-input-module`}>
              {MODULES.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <label>
            {locale === "en" ? "Linked dossier" : "Dossier lié"}
            <select value={form.dossier_lie} onChange={(e) => setField("dossier_lie", e.target.value)} data-testid={`${testId}-input-dossier`}>
              <option value="">{locale === "en" ? "— None —" : "— Aucun —"}</option>
              {dossiers.map((d) => (
                <option key={d.id} value={d.id}>{localizeDemoText(d.sujet_nom, locale)}</option>
              ))}
            </select>
          </label>
          <label className="form-grid__wide">
            {locale === "en" ? "Notes" : "Notes"}
            <input value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid={`${testId}-input-notes`} />
          </label>
        </div>
        <div className="btn-row">
          <button type="button" className="btn-primary btn-sm" onClick={handlePreview} data-testid={`${testId}-btn-preview`}>
            {locale === "en" ? "Prepare preview" : "Préparer aperçu"}
          </button>
        </div>
      </GsdReadableBlock>

      {preview ? (
        <GsdReadableBlock testId={`${testId}-preview`}>
          <h4>{locale === "en" ? "Preview before import" : "Aperçu avant import"}</h4>
          <p data-testid={`${testId}-preview-notice`}>
            {locale === "en" ? IMPORT_PREVIEW_NOTICE_EN : IMPORT_PREVIEW_NOTICE_FR}
          </p>
          <p data-testid={`${testId}-preview-official-notice`} className="muted">
            {locale === "en" ? IMPORT_PREVIEW_OFFICIAL_NOTICE_EN : IMPORT_PREVIEW_OFFICIAL_NOTICE_FR}
          </p>
          <ul data-testid={`${testId}-preview-summary`}>
            <li>{locale === "en" ? "File name" : "Nom du fichier"} : {localizeDemoText(preview.fichier_nom, locale) || "—"}</li>
            <li>{locale === "en" ? "Type" : "Type"} : {authDocLabel(DOCUMENT_TYPES, preview.type_document, locale)}</li>
            <li>{locale === "en" ? "Category" : "Catégorie"} : {(CATEGORIES[preview.categorie] || {})[locale] ?? preview.categorie}</li>
            <li>{locale === "en" ? "Sensitivity" : "Sensibilité"} : {authDocLabel(SENSITIVITY_LEVELS, preview.sensibilite, locale)}</li>
            <li>{locale === "en" ? "Linked module" : "Module lié"} : {localizeDemoText(preview.module_lie, locale)}</li>
            <li>{locale === "en" ? "Linked dossier" : "Dossier lié"} : {preview.dossier_lie || (locale === "en" ? "None" : "Aucun")}</li>
            <li data-testid={`${testId}-preview-initial-status`}>
              {locale === "en" ? "Initial status" : "Statut initial"} :{" "}
              <strong>{locale === "en" ? "Received — validation required" : "Reçu — validation requise"}</strong>
            </li>
          </ul>

          <h5>{locale === "en" ? "Warnings" : "Avertissements"}</h5>
          <ul data-testid={`${testId}-preview-warnings`}>
            {preview.warnings.map((w, i) => (
              <li key={i} data-testid={`${testId}-warning-${i}`}>
                <StatusBadge label={w.niveau} tone={w.niveau === "attention" ? "yellow" : "blue"} /> {w.message}
              </li>
            ))}
          </ul>

          <h5>{locale === "en" ? "What will be added" : "Ce qui sera ajouté"}</h5>
          <p className="muted">
            {locale === "en"
              ? "One document metadata entry with status received — validation required, plus a journal entry."
              : "Une entrée de métadonnées de document au statut reçu — validation requise, plus une entrée de journal."}
          </p>
          <h5>{locale === "en" ? "What will not be done" : "Ce qui ne sera pas fait"}</h5>
          <ul className="muted">
            <li>{locale === "en" ? "No real file stored" : "Aucun fichier réel stocké"}</li>
            <li>{locale === "en" ? "No automatic validation" : "Aucune validation automatique"}</li>
            <li>{locale === "en" ? "No official recognition" : "Aucune reconnaissance officielle"}</li>
            <li>{locale === "en" ? "No PDF certificate" : "Aucun certificat PDF"}</li>
          </ul>

          <div className="btn-row">
            <button type="button" className="btn-primary btn-sm" onClick={handleConfirm} data-testid={`${testId}-btn-confirm`}>
              {locale === "en" ? "Confirm local import" : "Confirmer import local"}
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={handleCancel} data-testid={`${testId}-btn-cancel`}>
              {locale === "en" ? "Cancel" : "Annuler"}
            </button>
          </div>
        </GsdReadableBlock>
      ) : null}

      <ImportQueuePanel testId={`${testId}-queue`} />
    </div>
  );
}

/** File d'import (métadonnées) — tableau recherche/tri/filtres. */
export function ImportQueuePanel({ testId = "import-queue-panel" }) {
  const locale = useLocale();
  const store = useDataStore();

  const rows = useMemo(
    () =>
      (store.documentImportQueue || []).map((r) => ({
        ...r,
        statutLabel: authDocLabel(IMPORT_STATUSES, r.statut_import, locale),
        typeLabel: authDocLabel(DOCUMENT_TYPES, r.type_document_propose, locale),
        sensLabel: authDocLabel(SENSITIVITY_LEVELS, r.sensibilite_proposee, locale),
      })),
    [store.documentImportQueue, locale],
  );

  const columns = [
    {
      key: "fichier_nom",
      label: locale === "en" ? "File" : "Fichier",
      render: (r) => {
        const { text, showBadge } = formatImportFileDisplay(r.fichier_nom, locale, r.demo);
        return (
          <>
            {text}
            {showBadge ? <> <DemoBadge /></> : null}
          </>
        );
      },
    },
    { key: "typeLabel", label: locale === "en" ? "Type" : "Type" },
    {
      key: "module_lie",
      label: locale === "en" ? "Module" : "Module",
      render: (r) => localizeDemoText(r.module_lie, locale),
    },
    { key: "sensLabel", label: locale === "en" ? "Sensitivity" : "Sensibilité" },
    {
      key: "statutLabel",
      label: locale === "en" ? "Import status" : "Statut import",
      render: (r) => <StatusBadge label={r.statutLabel} tone="blue" testId={`import-status-${r.id}`} />,
    },
    { key: "date_ajout", label: locale === "en" ? "Added" : "Ajout" },
  ];

  return (
    <div data-testid={testId}>
      <h4>{locale === "en" ? "Import queue" : "File d'import"}</h4>
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["fichier_nom", "typeLabel", "module_lie"]}
        filters={[
          {
            field: "statut_import",
            label: locale === "en" ? "Import status" : "Statut import",
            options: Object.entries(IMPORT_STATUSES).map(([v, l]) => ({ value: v, label: locale === "en" ? l.en : l.fr })),
          },
          {
            field: "module_lie",
            label: locale === "en" ? "Module" : "Module",
            options: MODULES.map((m) => ({ value: m, label: m })),
          },
        ]}
        emptyMessage={locale === "en" ? "No imports." : "Aucun import."}
        testId={`${testId}-table`}
      />
    </div>
  );
}

/** Section pédagogique backend sécurisé (intégrée dans Sécurité future). */
export function SecureBackendBlueprintPanel({ testId = "secure-backend-blueprint-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const blueprint = store.secureBackendBlueprint || null;

  const requirements = blueprint?.requirements ?? [];
  const roles = blueprint?.roles ?? [];
  const migration = blueprint?.migration ?? [];

  return (
    <div data-testid={testId}>
      <GsdReadableBlock testId={`${testId}-intro`}>
        <h3>{locale === "en" ? "Future secure backend" : "Backend sécurisé futur"}</h3>
        <p className="muted" data-testid={`${testId}-notice`}>
          {locale === "en"
            ? "Plan for the future backend required to handle real sensitive documents. Not implemented in Phase 1."
            : "Plan du futur backend requis pour traiter de vrais documents sensibles. Non implémenté en Phase 1."}
        </p>
      </GsdReadableBlock>

      <GsdReadableBlock testId={`${testId}-requirements`}>
        <h4>{locale === "en" ? "Required backend" : "Backend futur requis"}</h4>
        <ul data-testid={`${testId}-requirements-list`}>
          {requirements.map((r) => (
            <li key={r.key}>{locale === "en" ? r.en : r.fr}</li>
          ))}
        </ul>
      </GsdReadableBlock>

      <GsdReadableBlock testId={`${testId}-roles`}>
        <h4>{locale === "en" ? "Proposed roles" : "Rôles proposés"}</h4>
        <table className="gsd-table" data-testid={`${testId}-roles-table`}>
          <thead>
            <tr>
              <th>{locale === "en" ? "Role" : "Rôle"}</th>
              <th>{locale === "en" ? "Access" : "Accès"}</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.role}>
                <td>{r.role}</td>
                <td>{locale === "en" ? r.en : r.fr}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </GsdReadableBlock>

      <GsdReadableBlock testId={`${testId}-migration`}>
        <h4 data-testid={`${testId}-migration-title`}>
          {locale === "en" ? "localStorage to backend migration" : "Migration localStorage vers backend"}
        </h4>
        <ol data-testid={`${testId}-migration-list`}>
          {migration.map((m) => (
            <li key={m.step}>{locale === "en" ? m.en : m.fr}</li>
          ))}
        </ol>
      </GsdReadableBlock>
    </div>
  );
}
